import React, { useState, useEffect } from 'react';  
import { motion } from 'framer-motion';  
import { ArrowLeft, Image, Home, CheckCircle2 } from 'lucide-react';  
import { useParams, useNavigate } from 'react-router-dom';  
import propiedades from '../data/propiedades.json';  
import Header from './Header';  
import Footer from './Footer';
import PanoramaViewer360 from './PanoramaViewer360';

const PropertyDetail = () => {  
  const { id } = useParams();  
  const navigate = useNavigate();  
  const [propiedad, setPropiedad] = useState(null);  
  const [currentImage, setCurrentImage] = useState(0); 

  useEffect(() => {  
    const prop = propiedades.find(p => p.id === parseInt(id));  
    if (prop && prop.visible !== false) {  
      setPropiedad(prop);  
      setCurrentImage(0);  
    } else {  
      navigate('/');  
    }  
  }, [id, navigate]); 

  if (!propiedad) return <div className="flex justify-center items-center h-64 text-gray-500">Cargando...</div>;  

  const LocationIcon = () => (  
    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />  
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />  
    </svg>  
  );  

  const RulerIcon = () => (  
    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z" />  
    </svg>  
  );  

  const BathIcon = () => (  
    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2-2 2 2 0 00-2 2v5a2 2 0 01-2 2z" />  
    </svg>  
  );  

  const LayersIcon = () => (  
    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />  
    </svg>  
  );  

  const DollarIcon = () => (  
    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />  
    </svg>  
  );  

  return (  
    <>  
      <div className="min-h-screen bg-gray-50">  
        <Header />  
        <motion.div  
          className="py-8"  
          initial={{ opacity: 0 }}  
          animate={{ opacity: 1 }}  
          transition={{ duration: 0.5 }}  
        >  
          <div className="container mx-auto px-4 max-w-6xl">  
          <button  
            onClick={() => navigate('/')}  
            className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800 transition-colors"  
          >  
            <ArrowLeft className="w-5 h-5" />  
            Volver al listado  
          </button>  

          <div className="grid md:grid-cols-2 gap-8 mb-8">  
            <div className="space-y-4">  
              <img  
                src={propiedad.imagenes[currentImage]}  
                alt={propiedad.nombre}  
                className="w-full rounded-xl shadow-lg aspect-video object-cover"  
              />  
              {propiedad.imagenes.length > 1 && (  
                <div className="flex flex-wrap gap-2">  
                  {propiedad.imagenes.map((img, idx) => (  
                    <img  
                      key={idx}  
                      src={img}  
                      alt={`Vista ${idx + 1}`}  
                      className={`w-16 sm:w-20 aspect-square object-cover rounded-lg cursor-pointer transition-all ${currentImage === idx ? 'ring-2 ring-blue-500 shadow-md' : 'hover:ring-1 hover:shadow-sm opacity-70 hover:opacity-100'}`}  
                      onClick={() => setCurrentImage(idx)}  
                    />  
                  ))}  
                </div>  
              )}  
            </div>  

            <div className="space-y-6">  
              <div>  
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{propiedad.nombre}</h1>  
                <div className="flex items-center gap-2 text-blue-600 mb-4">  
                  <Home className="w-5 h-5" />  
                  <span className="font-semibold">{propiedad.tipo}</span>  
                </div>  
                <div className="flex items-center gap-2 text-gray-500 mb-4">  
                  <LocationIcon />  
                  <span className="font-medium">{propiedad.ubicacion}</span>  
                </div>  
                <p className="text-gray-700 leading-relaxed mb-6">{propiedad.descripcion_larga}</p>  
              </div>  

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-white rounded-xl shadow-sm">  
                <div className="flex flex-col items-center text-center">  
                  <RulerIcon className="w-6 h-6 text-blue-600 mb-2" />  
                  <span className="font-bold text-lg">{propiedad.metros} m²</span>  
                  <span className="text-gray-500 text-sm">Superficie</span>  
                </div>  
                <div className="flex flex-col items-center text-center">  
                  <BathIcon className="w-6 h-6 text-blue-600 mb-2" />  
                  <span className="font-bold text-lg">{propiedad.banos}</span>  
                  <span className="text-gray-500 text-sm">Baños</span>  
                </div>  
                <div className="flex flex-col items-center text-center">  
                  <LayersIcon className="w-6 h-6 text-blue-600 mb-2" />  
                  <span className="font-bold text-lg">{propiedad.pisos}</span>  
                  <span className="text-gray-500 text-sm">Pisos</span>  
                </div>  
              </div>  

            </div>  
          </div>  

          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">  
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">  
              <Home className="w-5 h-5" />  
              Servicios Incluidos  
            </h3>  
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">  
              {propiedad.servicios.map((servicio, idx) => (  
                <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">  
                  <CheckCircle2 className="w-4 h-4 text-green-500" />  
                  <span className="text-sm text-gray-700">{servicio}</span>  
                </div>  
              ))}  
            </div>  
          </div>  

          {propiedad.tour360s && propiedad.tour360s.length > 0 && (
            <PanoramaViewer360 scenes={propiedad.tour360s} />
          )}

          {!propiedad.tour360s && propiedad.tour360 && (
            <div className="flex justify-center mt-8">
              <motion.button
                onClick={() => navigate(`/propiedad/${id}/360`)}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Image className="w-5 h-5 inline mr-2" />
                Ver Recorrido 360°
              </motion.button>
            </div>
          )}

          <div className="flex justify-center mt-6">  
            <DollarIcon className="w-6 h-6 text-gray-500 mr-2" />  
            <span className="text-xl font-semibold text-gray-700">{propiedad.precio}</span>  
          </div>  
          </div>  
        </motion.div>  
      </div>  
      <Footer />  
    </>  
  );  
};  

export default PropertyDetail;
